<template>
  <v-form ref="form" autocomplete="off" @submit.prevent="save">
    <v-row>
      <!-- Sección: Identificación (marca, modelo, tamaño, códigos) -->
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-medium primary--text mb-2 pt-2">
          Identificación
        </div>
        <v-divider></v-divider>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          prepend-icon="title"
          name="nombre"
          v-model="form.nombre"
          label="Nombre del producto"
          :placeholder="nombreCalculado || 'Marca + modelo + tamaño'"
          :readonly="form.nombreAutogenerado"
          :hint="form.nombreAutogenerado ? 'Se actualiza al cambiar marca, modelo o tamaño' : 'Nombre fijo: no se modifica al cambiar marca, modelo o tamaño'"
          persistent-hint
          color="secondary"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <marca-combobox v-model="form.marca" :input-errors="inputErrors" />
      </v-col>
      <v-col cols="12" sm="6">
        <modelo-combobox
          v-model="form.modelo"
          :input-errors="inputErrors"
          :marca="form.marca"
        />
      </v-col>
      <v-col cols="12" sm="6">
        <tamanio-combobox v-model="form.tamanio" :input-errors="inputErrors" />
      </v-col>
      <v-col cols="12">
        <v-checkbox
          v-model="form.nombreAutogenerado"
          label="Nombre automático (marca + modelo + tamaño)"
          color="secondary"
          hide-details
          class="mt-0 pt-0"
          @change="onNombreAutogeneradoChange"
        ></v-checkbox>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          prepend-icon="notes"
          name="otrasCaracteristicas"
          v-model="form.otrasCaracteristicas"
          :label="$t('producto.labels.otrasCaracteristicas')"
          :placeholder="$t('producto.labels.otrasCaracteristicas')"
          :error="hasInputErrors('otrasCaracteristicas')"
          :error-messages="getInputErrors('otrasCaracteristicas')"
          color="secondary"
        ></v-text-field>
      </v-col>
      <!-- Sección: Códigos de barras por proveedor -->
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-medium primary--text mb-2 mt-2">
          Códigos de barras por proveedor
        </div>
        <v-divider></v-divider>
      </v-col>
      <v-col v-if="errorCodigoProveedor" cols="12">
        <v-alert type="error" dense text dismissible @input="errorCodigoProveedor = ''">
          {{ errorCodigoProveedor }}
        </v-alert>
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          prepend-icon="mdi-truck"
          v-model="nuevoCodigo.proveedorId"
          :items="proveedoresDisponibles"
          item-value="id"
          item-text="nombre"
          label="Proveedor"
          :disabled="modoEdicionCodigo"
          :loading="loadingProveedores"
          color="secondary"
          clearable
          @change="nuevoCodigo.codigoBarrasProveedor = ''"
        />
      </v-col>
      <v-col cols="12" sm="4">
        <v-text-field
          prepend-icon="mdi-barcode"
          v-model="nuevoCodigo.codigoBarrasProveedor"
          label="Código de barras del proveedor"
          color="secondary"
          @keyup.enter="guardarNuevoCodigo"
        />
      </v-col>
      <v-col cols="12" sm="2" class="d-flex align-center">
        <v-btn small color="primary" :loading="guardandoCodigo" @click="guardarNuevoCodigo" class="mr-2">
          {{ modoEdicionCodigo ? 'Actualizar' : 'Guardar' }}
        </v-btn>
        <v-btn small text @click="cancelarEdicionCodigo">Cancelar</v-btn>
      </v-col>
      <v-col cols="12" sm="6" v-if="preciosProveedor.length > 0">
        <v-select
          prepend-icon="mdi-account-check"
          :value="form.productoProveedorPrecioActivoId"
          @input="onProveedorActivoChange"
          :items="preciosProveedor"
          item-value="id"
          :item-text="getCodigoProveedorSelectLabel"
          label="Proveedor activo (para precio de compra y cálculo)"
          hint="Se usa para el cálculo del precio de venta"
          persistent-hint
          color="secondary"
          clearable
        >
          <template v-slot:item="{ item }">
            <v-list-item-content>
              <v-list-item-title>{{ item.codigoBarrasProveedor || '-' }}</v-list-item-title>
              <v-list-item-subtitle>
                {{ item.proveedor && item.proveedor.nombre ? item.proveedor.nombre : 'Sin proveedor' }} · Precio compra: {{ formatPrecioCompraItem(item) }}
              </v-list-item-subtitle>
            </v-list-item-content>
            <v-list-item-action>
              <v-btn icon small color="error" @click.stop="eliminarCodigoProveedor(item)">
                <v-icon small>mdi-delete</v-icon>
              </v-btn>
            </v-list-item-action>
          </template>
        </v-select>
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          prepend-icon="label"
          v-model="modoCodigoBarras"
          :items="opcionesModoCodigoBarras"
          label="Estrategia para generar codigo de barras propio"
          persistent-hint
          color="secondary"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          prepend-icon="label"
          name="codigoBarras"
          v-model="form.codigoBarras"
          :label="$t('producto.labels.codigoBarrasPropio')"
          :placeholder="$t('producto.labels.codigoBarras')"
          :error="hasInputErrors('codigoBarras')"
          :error-messages="getInputErrors('codigoBarras')"
          :readonly="modoCodigoBarras === 'autogenerar'"
          :hint="modoCodigoBarras !== 'manual' && preciosProveedorParaModo.length === 0 ? 'Ingrese proveedor y código de barras del proveedor arriba' : ''"
          persistent-hint
          color="secondary"
          @blur="form.codigoBarras = normalizarCodigoBarras(form.codigoBarras)"
        ></v-text-field>
      </v-col>

      <!-- Dialog confirmar eliminar código proveedor -->
      <v-dialog v-model="dialogConfirmarEliminarCodigo" max-width="400" persistent>
        <v-card>
          <v-card-title class="text-subtitle-1">Eliminar código de proveedor</v-card-title>
          <v-card-text>
            <template v-if="codigosProveedorAEliminar.length === 1">
              ¿Eliminar este código de proveedor?
              <span v-if="codigosProveedorAEliminar[0]" class="font-weight-medium d-block mt-1">
                {{ codigosProveedorAEliminar[0].codigoBarrasProveedor }}
                <span v-if="codigosProveedorAEliminar[0].proveedor && codigosProveedorAEliminar[0].proveedor.nombre" class="grey--text text--darken-1">
                  ({{ codigosProveedorAEliminar[0].proveedor.nombre }})
                </span>
              </span>
            </template>
            <template v-else>
              ¿Eliminar los {{ codigosProveedorAEliminar.length }} códigos seleccionados?
            </template>
          </v-card-text>
          <v-card-actions>
            <v-spacer />
            <v-btn text color="secondary" @click="dialogConfirmarEliminarCodigo = false; codigosProveedorAEliminar = []">Cancelar</v-btn>
            <v-btn color="error" @click="confirmarEliminarCodigosProveedor">Eliminar</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Sección: IVA y precios -->
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-medium primary--text mb-2 mt-2">
          IVA y precios
        </div>
        <v-divider></v-divider>
      </v-col>
      <v-col
        cols="12"
        sm="6"
        v-if="formulaActivaIncluye('precio_compra') && (operation === 'creating' || pppActivo || preciosProveedor.length > 0)"
      >
        <v-text-field
          prepend-icon="attach_money"
          v-model.number="precioCompraActivoEdit"
          type="number"
          step="any"
          :min="0"
          :label="$t('producto.labels.precioCompra')"
          hint="Precio de compra del proveedor activo; modifica el cálculo del precio de venta"
          persistent-hint
          color="secondary"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" v-if="formulaActivaIncluye('descuento')">
        <v-text-field
          prepend-icon="local_offer"
          name="descuentoProveedor"
          v-model.number="form.descuentoProveedor"
          type="number"
          :label="$t('producto.labels.descuentoProveedor')"
          suffix="%"
          hint="Descuento aplicado al costo (producto)"
          persistent-hint
          :min="0"
          :max="100"
          color="secondary"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6" v-if="formulaActivaIncluye('iva')">
        <v-select
          prepend-icon="account_balance"
          name="ivaPorcentaje"
          v-model.number="form.ivaPorcentaje"
          :items="ivaOptions"
          :label="$t('producto.labels.ivaPorcentaje')"
          hint="Recargo de IVA aplicable al producto"
          persistent-hint
          color="secondary"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" v-if="formulaActivaIncluye('margen')">
        <v-text-field
          prepend-icon="trending_up"
          name="porcentajeSobreCosto"
          v-model.number="margenCampo"
          type="number"
          :label="operation === 'updating' ? 'Margen override de la lista' : $t('producto.labels.porcentajeSobreCosto')"
          suffix="%"
          :hint="margenCampoHint"
          persistent-hint
          :min="0"
          color="secondary"
          clearable
          @click:clear="margenCampo = null"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-select
          prepend-icon="mdi-currency-usd"
          name="tipoRedondeo"
          v-model="tipoRedondeo"
          :items="opcionesRedondeo"
          :label="$t('producto.labels.tipoRedondeo')"
          hint="Aplica redondeo al precio final calculado"
          persistent-hint
          color="secondary"
        ></v-select>
      </v-col>
      <v-col cols="12" sm="6" v-if="operation === 'updating' && precioListaActiva">
        <v-text-field
          prepend-icon="mdi-cash"
          v-model.number="precioVentaCampo"
          type="number"
          step="0.01"
          min="0"
          label="Precio venta por lista"
          prefix="$"
          :hint="precioVentaCampoHint"
          persistent-hint
          color="secondary"
          clearable
          @click:clear="precioVentaCampo = null"
          @blur="precioVentaCampo = precioVentaCampo"
        ></v-text-field>
      </v-col>
      <v-col cols="12" v-if="operation === 'updating'">
        <producto-precios-lista-panel
          ref="preciosListaPanel"
          :producto-id="form.id || form._id || null"
          :producto="form"
          @active-change="onPrecioListaActivaChange"
        >
          <template v-slot:preview="{ precioLista }">
            <v-card outlined color="blue-grey lighten-5" class="mb-4 precio-preview-card">
              <v-card-text>
                <div class="text-subtitle-2 mb-2">
                  Cálculo del Precio de Venta
                </div>
                <div class="text-caption" v-if="precioCompraParaCalculo != null && precioCompraParaCalculo > 0">
                  <div>Precio base (proveedor activo): ${{ formatCurrency(precioCompraParaCalculo) }}</div>
                  <div v-if="previewListaRow(precioLista, 'descuento')">
                    Desc. proveedor (-{{ descuentoProveedorParaCalculo }}%):
                    -${{ formatCurrency(previewListaValores(precioLista).montoDescuento) }}
                  </div>
                  <div v-if="previewListaRow(precioLista, 'iva')">
                    IVA (+{{ form.ivaPorcentaje }}%):
                    +${{ formatCurrency(previewListaValores(precioLista).montoIva) }}
                  </div>
                  <div v-if="previewListaRow(precioLista, 'margen')">
                    Margen (+{{ previewListaValores(precioLista).margen }}%):
                    +${{ formatCurrency(previewListaValores(precioLista).montoMargen) }}
                  </div>
                  <div v-if="previewListaValores(precioLista).montoRedondeo > 0">
                    Redondeo ({{ obtenerTextoRedondeo(previewListaValores(precioLista).tipoRedondeo) }}):
                    +${{ formatCurrency(previewListaValores(precioLista).montoRedondeo) }}
                  </div>
                  <v-divider class="my-2"></v-divider>
                  <div class="text-h6 primary--text">
                    Precio Final estimado: ${{ formatCurrency(precioFinalLista(precioLista)) }}
                  </div>
                  <div class="formula-preview-aplicada mt-2">
                    <div class="font-weight-medium mb-1">Fórmula aplicada</div>
                    <code class="formula-preview-code">{{ formulaAplicadaPreview(precioLista) }}</code>
                  </div>
                </div>
                <div v-else class="precio-preview-warning">
                  Seleccione un proveedor activo con precio de compra para ver el cálculo
                </div>
              </v-card-text>
            </v-card>
          </template>
        </producto-precios-lista-panel>
      </v-col>
      <v-col cols="12" v-else>
        <v-card outlined color="blue-grey lighten-5">
          <v-card-text>
            <div class="text-subtitle-2 mb-2">
              Cálculo del Precio de Venta
            </div>
            <div class="text-caption" v-if="precioCompraParaCalculo != null && precioCompraParaCalculo > 0">
              <div>Precio base (proveedor activo): ${{ formatCurrency(precioCompraParaCalculo) }}</div>
              <div v-if="descuentoProveedorParaCalculo > 0">
                Desc. proveedor (-{{ descuentoProveedorParaCalculo }}%):
                -${{ formatCurrency(calcularMontoDescuento) }}
              </div>
              <div v-if="form.ivaPorcentaje > 0">
                IVA (+{{ form.ivaPorcentaje }}%):
                +${{ formatCurrency(calcularMontoIva) }}
              </div>
              <div v-if="form.porcentajeSobreCosto > 0">
                Margen (+{{ form.porcentajeSobreCosto }}%):
                +${{ formatCurrency(calcularMontoMargen) }}
              </div>
              <div v-if="tipoRedondeo && tipoRedondeo !== 'ninguno' && precioVentaCalculadoBase > 0">
                Redondeo ({{ obtenerTextoRedondeo() }}):
                +${{ formatCurrency(calcularMontoRedondeo) }}
              </div>
              <v-divider class="my-2"></v-divider>
              <div class="text-h6 primary--text">
                Precio Final estimado: ${{ formatCurrency(precioVentaCalculado) }}
              </div>
            </div>
            <div v-else class="precio-preview-warning">
              Seleccione un proveedor activo con precio de compra para ver el cálculo
            </div>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Sección: Stock y vencimiento -->
      <v-col cols="12">
        <div class="text-subtitle-1 font-weight-medium primary--text mb-2 mt-2">
          Stock y vencimiento
        </div>
        <v-divider></v-divider>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          required
          prepend-icon="label"
          name="stock"
          v-model.number="form.stock"
          type="number"
          :rules="mayorIgualCero"
          :label="$t('producto.labels.stock')"
          :placeholder="$t('producto.labels.stock')"
          :error="hasInputErrors('stock')"
          :error-messages="getInputErrors('stock')"
          color="secondary"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <v-text-field
          prepend-icon="label"
          name="stockAlerta"
          v-model.number="form.stockAlerta"
          type="number"
          :rules="mayorCeroOpcional"
          :label="$t('producto.labels.stockAlerta')"
          :placeholder="$t('producto.labels.stockAlerta')"
          :error="hasInputErrors('stockAlerta')"
          :error-messages="getInputErrors('stockAlerta')"
          color="secondary"
        ></v-text-field>
      </v-col>
      <v-col cols="12" sm="6">
        <date-input
          v-model="form.fechaVencimiento"
          :label="$t('producto.labels.fechaVencimiento')"
          prepend-icon="event"
          color="secondary"
          :error="hasInputErrors('fechaVencimiento')"
          :error-messages="getInputErrors('fechaVencimiento')"
        />
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
import { InputErrorsByProps, RequiredRule } from "@dracul/common-frontend";

import ProductoProvider from "../../../providers/ProductoProvider";
import ProveedorProvider from "../../../../proveedor/providers/ProveedorProvider";
import ListaPreciosVentaProvider from "../../../../lista-precios-venta/providers/ListaPreciosVentaProvider";
import MarcaCombobox from "./MarcaCombobox";
import ModeloCombobox from "./ModeloCombobox";
import TamanioCombobox from "./TamanioCombobox";
import ProductoPreciosListaPanel from "./ProductoPreciosListaPanel.vue";

import { DayjsMixin, DateInput } from "@dracul/dayjs-frontend";

export default {
  name: "ProductoForm",
  mixins: [InputErrorsByProps, RequiredRule, DayjsMixin],
  components: {
    MarcaCombobox,
    ModeloCombobox,
    TamanioCombobox,
    DateInput,
    ProductoPreciosListaPanel,
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    operation: null,
  },
  data() {
    return {
      mayorCero: [
        (v) => parseFloat(v) > 0 || "El valor ingresado debe ser mayor a 0",
      ],
      mayorIgualCero: [
        (v) => parseFloat(v) >= 0 || "El valor ingresado debe ser mayor o igual a 0",
      ],
      mayorCeroOpcional: [
        (v) =>
          v == null ||
          v === "" ||
          Number.isNaN(parseFloat(v)) ||
          parseFloat(v) >= 0 ||
          "Si se indica, debe ser mayor o igual a 0",
      ],
      ivaOptions: [
        { text: '0%', value: 0 },
        { text: '10.5%', value: 10.5 },
        { text: '21%', value: 21 },
        { text: '27%', value: 27 }
      ],
      tipoRedondeo: null,
      opcionesRedondeo: [
        { text: 'Sin definir (usa default)', value: null },
        { text: 'Sin redondeo', value: 'ninguno' },
        { text: 'Redondear centavos (ej: 115.14 → 116.00)', value: 'centavos' },
        { text: 'Redondear a múltiplo de 10 (ej: 115.14 → 120.00)', value: 'multiplo_10' },
        { text: 'Redondear a múltiplo de 100 (ej: 115.14 → 200.00)', value: 'multiplo_100' }
      ],
      modoCodigoBarras: 'autogenerar', // 'autogenerar' | 'usarProveedor' | 'manual'
      opcionesModoCodigoBarras: [
        { text: 'Autogenerar (marca + código proveedor + tamaño)', value: 'autogenerar' },
        { text: 'Usar el mismo que el código del proveedor', value: 'usarProveedor' },
        { text: 'Escribir manualmente', value: 'manual' }
      ],
      preciosProveedor: [],
      nuevoCodigo: {
        proveedorId: null,
        codigoBarrasProveedor: "",
      },
      guardandoCodigo: false,
      modoEdicionCodigo: false,
      editandoCodigoId: null,
      proveedores: [],
      loadingProveedores: false,
      errorCodigoProveedor: "",
      dialogConfirmarEliminarCodigo: false,
      codigosProveedorAEliminar: [],
      precioCompraActivoEdit: null,
      precioListaActiva: null,
      margenOverrideActivoEdit: null,
      margenOverrideDirty: false,
      precioVentaOverrideActivoEdit: null,
      precioVentaOverrideDirty: false,
    };
  },
  computed: {
    form: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit("input", val);
      },
    },
    margenCampo: {
      get() {
        return this.operation === "updating"
          ? this.margenOverrideActivoEdit
          : this.form.porcentajeSobreCosto;
      },
      set(val) {
        const next = val === "" || val == null ? null : Number(val);
        if (this.operation === "updating") {
          this.margenOverrideActivoEdit = next;
          this.margenOverrideDirty = true;
        } else {
          this.form.porcentajeSobreCosto = next;
        }
      },
    },
    precioVentaCampo: {
      get() {
        return this.precioVentaOverrideActivoEdit;
      },
      set(val) {
        this.precioVentaOverrideActivoEdit =
          val === "" || val == null ? null : this.truncarDosDecimales(Number(val));
        this.precioVentaOverrideDirty = true;
      },
    },
    margenCampoHint() {
      if (this.operation !== "updating") return "Porcentaje sobre el costo";
      return "Se aplica al guardar el producto";
    },
    precioVentaCampoHint() {
      const calculado = this.precioListaActiva?.precioVentaCalculado;
      if (calculado == null) return "Se aplica al guardar el producto";
      return `Calculado por fórmula: $${this.formatCurrency(calculado)}. Se aplica al guardar el producto.`;
    },
    nombreCalculado() {
      const marcaStr = this.form.marca && (this.form.marca.nombre || "");
      const modeloStr = this.form.modelo && (this.form.modelo.nombre || "");
      const tamanioStr = this.form.tamanio && (this.form.tamanio.tamanio != null ? String(this.form.tamanio.tamanio) : "");
      const parts = [marcaStr, modeloStr, tamanioStr].filter(Boolean);
      return parts.length > 0 ? parts.join(" ") : "";
    },
    proveedoresDisponibles() {
      const asignados = this.preciosProveedor
        .filter((p) => p.id !== this.editandoCodigoId)
        .map((p) => p.proveedor?.id || p.proveedorId)
        .filter(Boolean);
      return this.proveedores.filter((p) => !asignados.includes(p.id));
    },
    preciosProveedorParaModo() {
      return this.preciosProveedor;
    },
    opcionesCodigoBarrasAutogenerado() {
      const marca = this.form.marca;
      const tamanio = this.form.tamanio;
      if (!marca || !tamanio || this.preciosProveedorParaModo.length === 0) return [];
      return this.preciosProveedorParaModo.map((pp) => {
        const codigo = this.generarCodigoBarrasPropio(marca, tamanio, pp.codigoBarrasProveedor || "");
        return {
          codigoBarras: this.normalizarCodigoBarras(codigo),
          pp,
        };
      });
    },
    pppActivo() {
      const activoId = this.form.productoProveedorPrecioActivoId;
      if (!activoId || !this.preciosProveedor.length) return null;
      const idStr = String(activoId);
      return this.preciosProveedor.find((p) => String(p.id) === idStr) || null;
    },
    precioCompraParaCalculo() {
      if (this.precioCompraActivoEdit != null && this.precioCompraActivoEdit !== "") return Number(this.precioCompraActivoEdit);
      if (this.pppActivo && this.pppActivo.precioCompra != null) return this.pppActivo.precioCompra;
      return this.form.precioCompra != null ? this.form.precioCompra : null;
    },
    descuentoProveedorParaCalculo() {
      return this.form.descuentoProveedor != null ? this.form.descuentoProveedor : 0;
    },
    calcularMontoDescuento() {
      const base = this.precioCompraParaCalculo;
      if (!base) return 0;
      const descuento = this.descuentoProveedorParaCalculo || 0;
      return base * (descuento / 100);
    },
    calcularPrecioDespuesDescuento() {
      const base = this.precioCompraParaCalculo;
      if (!base) return 0;
      const descuento = this.descuentoProveedorParaCalculo || 0;
      return base * (1 - descuento / 100);
    },
    calcularMontoIva() {
      const precioConDescuento = this.calcularPrecioDespuesDescuento;
      const iva = this.form.ivaPorcentaje || 0;
      return precioConDescuento * (iva / 100);
    },
    calcularPrecioDespuesIva() {
      const precioConDescuento = this.calcularPrecioDespuesDescuento;
      const iva = this.form.ivaPorcentaje || 0;
      return precioConDescuento * (1 + iva / 100);
    },
    calcularMontoMargen() {
      const precioConIva = this.calcularPrecioDespuesIva;
      const margen = this.form.porcentajeSobreCosto || 0;
      return precioConIva * (margen / 100);
    },
    precioVentaCalculadoBase() {
      const precioConIva = this.calcularPrecioDespuesIva;
      const margen = this.form.porcentajeSobreCosto || 0;
      return precioConIva * (1 + margen / 100);
    },
    calcularMontoRedondeo() {
      const precioBase = this.precioVentaCalculadoBase;
      if (precioBase > 0 && this.tipoRedondeo && this.tipoRedondeo !== 'ninguno') {
        let precioRedondeado = precioBase;
        switch (this.tipoRedondeo) {
          case 'centavos':
            precioRedondeado = Math.ceil(precioBase);
            break;
          case 'multiplo_10':
            precioRedondeado = Math.ceil(precioBase / 10) * 10;
            break;
          case 'multiplo_100':
            precioRedondeado = Math.ceil(precioBase / 100) * 100;
            break;
        }
        return precioRedondeado - precioBase;
      }
      return 0;
    },
    precioVentaCalculado() {
      const precioBase = this.precioVentaCalculadoBase;
      if (precioBase > 0 && this.tipoRedondeo && this.tipoRedondeo !== 'ninguno') {
        switch (this.tipoRedondeo) {
          case 'centavos':
            return Math.ceil(precioBase);
          case 'multiplo_10':
            return Math.ceil(precioBase / 10) * 10;
          case 'multiplo_100':
            return Math.ceil(precioBase / 100) * 100;
        }
      }
      return precioBase;
    }
  },
  mounted() {
    this.$nextTick(() => this.actualizarCodigoBarrasSegunModo());
    this.cargarPreciosProveedor();
    this.cargarProveedores();
  },
  watch: {
    pppActivo: {
      handler(ppp) {
        if (ppp) {
          this.precioCompraActivoEdit = ppp.precioCompra != null ? ppp.precioCompra : null;
          if (ppp.descuentoProveedor != null) this.$set(this.value, 'descuentoProveedor', ppp.descuentoProveedor);
        } else if (this.preciosProveedor.length === 0) {
          // Solo limpiar si no quedan códigos/proveedores; si hay filas pero no activo (p. ej. select limpiado), conservar el valor editado
          this.precioCompraActivoEdit = null;
        }
      },
      immediate: true,
    },
    "value.id": {
      handler(newId) {
        if (newId && this.operation === "updating") {
          this.cargarPreciosProveedor();
        }
      },
    },
    value: {
      immediate: true,
      handler(val) {
        if (val && val.nombreAutogenerado) {
          this.$nextTick(() => {
            if (!this.form.nombreAutogenerado) return;
            this.sincronizarNombreAutogeneradoDesdeFormulario(val);
          });
        }
        // Inicializar tipo de redondeo desde el valor del formulario / backend (null = sin override)
        this.tipoRedondeo = val && val.tipoRedondeo != null ? val.tipoRedondeo : null;

        if (this.operation === 'creating') return;
        if (val && val.codigoBarras != null && val.codigoBarrasProveedor != null) {
          this.modoCodigoBarras = val.codigoBarras === val.codigoBarrasProveedor ? 'usarProveedor' : 'manual';
        }
        if (this.operation === 'updating' && val && Array.isArray(val.preciosProveedor) && val.preciosProveedor.length > 0) {
          this.preciosProveedor = val.preciosProveedor;
        }
      }
    },
    modoCodigoBarras() {
      this.actualizarCodigoBarrasSegunModo(true);
    },
    preciosProveedor() {
      if (this.modoCodigoBarras === 'usarProveedor' || this.modoCodigoBarras === 'autogenerar') {
        this.actualizarCodigoBarrasSegunModo();
      }
    },
    'form.marca': function () {
      this.actualizarCodigoBarrasSegunModo();
      this.sincronizarNombreAutogeneradoDesdeFormulario(this.value);
    },
    'form.tamanio': function () {
      this.actualizarCodigoBarrasSegunModo();
      this.sincronizarNombreAutogeneradoDesdeFormulario(this.value);
    },
    'form.modelo': function () {
      this.sincronizarNombreAutogeneradoDesdeFormulario(this.value);
    },
    'form.codigoBarrasProveedor': function () { this.actualizarCodigoBarrasSegunModo(); },
    'form.proveedorId': function () { this.actualizarCodigoBarrasSegunModo(); },
    tipoRedondeo() {
      this.syncPrecioVentaCalculadoActivo();
    },
    precioCompraActivoEdit() {
      this.syncPrecioVentaCalculadoActivo();
    },
    margenOverrideActivoEdit() {
      this.syncPrecioVentaCalculadoActivo();
    },
    'form.descuentoProveedor': function () {
      this.syncPrecioVentaCalculadoActivo();
    },
    'form.ivaPorcentaje': function () {
      this.syncPrecioVentaCalculadoActivo();
    }
  },
  methods: {
    onProveedorActivoChange(newId) {
      this.$set(this.value, 'productoProveedorPrecioActivoId', newId);
      this.$emit('input', this.value);
      const ppp = newId && this.preciosProveedor.length
        ? this.preciosProveedor.find((p) => String(p.id) === String(newId))
        : null;
      if (ppp) {
        this.precioCompraActivoEdit = ppp.precioCompra != null ? ppp.precioCompra : null;
        if (ppp.descuentoProveedor != null) this.$set(this.value, 'descuentoProveedor', ppp.descuentoProveedor);
      } else if (this.preciosProveedor.length === 0) {
        this.precioCompraActivoEdit = null;
      }
      this.$nextTick(() => this.$forceUpdate());
    },
    getPendingPppUpdate() {
      const productoId = this.form.id || this.form._id;
      if (!productoId) return null;
      const activoId = this.form.productoProveedorPrecioActivoId;
      if (!activoId) return null;
      const ppp = this.pppActivo || (this.preciosProveedor || []).find((p) => String(p.id) === String(activoId));
      if (!ppp) return null;
      const proveedorId = ppp.proveedor?.id || ppp.proveedor?._id || ppp.proveedorId;
      const codigoBarrasProveedor = (ppp.codigoBarrasProveedor || "").trim();
      if (!proveedorId || !codigoBarrasProveedor) return null;
      const precioCompra = this.precioCompraActivoEdit != null && this.precioCompraActivoEdit !== ""
        ? Number(this.precioCompraActivoEdit)
        : (ppp.precioCompra != null ? Number(ppp.precioCompra) : null);
      const descuentoProveedor = this.form.descuentoProveedor != null && this.form.descuentoProveedor !== "" ? Number(this.form.descuentoProveedor) : (ppp.descuentoProveedor != null ? Number(ppp.descuentoProveedor) : null);
      return {
        productoId: String(productoId),
        proveedorId: String(proveedorId),
        codigoBarrasProveedor,
        precioCompra,
        descuentoProveedor,
      };
    },
    onNombreAutogeneradoChange() {
      if (this.form.nombreAutogenerado) {
        this.sincronizarNombreAutogeneradoDesdeFormulario(this.value);
        return;
      }
      if (!(this.form.nombre && this.form.nombre.trim())) {
        this.form.nombre = this.nombreCalculado;
      }
    },
    /**
     * Con nombre automático: si hay marca/modelo/tamaño, el nombre es la concatenación.
     * Si no hay datos para calcularlo, no vaciar el campo (conservar nombre del ítem o lo escrito).
     */
    sincronizarNombreAutogeneradoDesdeFormulario(val) {
      if (!this.form.nombreAutogenerado) return;
      const nc = this.nombreCalculado && String(this.nombreCalculado).trim();
      if (nc) {
        this.form.nombre = nc;
        return;
      }
      const desdeVal =
        val && val.nombre != null && String(val.nombre).trim() !== ""
          ? String(val.nombre).trim()
          : "";
      if (desdeVal) {
        this.form.nombre = desdeVal;
        return;
      }
      // Sin cálculo ni nombre en val: no asignar "" — deja el contenido actual del formulario
    },
    normalizarCodigoBarras(val) {
      return (val || '').trim().replace(/\s+/g, ' ');
    },
    validate() {
      this.form.codigoBarras = this.normalizarCodigoBarras(this.form.codigoBarras);
      this.form.codigoBarrasProveedor = this.normalizarCodigoBarras(this.form.codigoBarrasProveedor);
      // Sincronizar tipo de redondeo con el payload (null = sin definir, usa default backend)
      this.form.tipoRedondeo = this.tipoRedondeo;
      return this.$refs.form.validate();
    },
    formatCurrency(value) {
      return parseFloat(value || 0).toFixed(2);
    },
    onPrecioListaActivaChange(precioLista) {
      this.precioListaActiva = precioLista;
      this.margenOverrideActivoEdit = this.margenEfectivoParaLista(precioLista);
      this.margenOverrideDirty = false;
      this.precioVentaOverrideDirty = false;
      this.precioVentaOverrideActivoEdit =
        precioLista?.precioVentaOverride != null
          ? this.truncarDosDecimales(Number(precioLista.precioVentaOverride))
          : this.resultadoFormulaPreview(precioLista) != null
          ? this.truncarDosDecimales(Number(this.resultadoFormulaPreview(precioLista)))
          : null;
    },
    margenEfectivoParaLista(precioLista) {
      return Number(
        precioLista?.margenOverride ??
        precioLista?.lista?.formulaConfig?.margenDefault ??
        this.form.porcentajeSobreCosto ??
        0
      );
    },
    syncPrecioVentaCalculadoActivo() {
      if (!this.precioListaActiva) return;
      if (this.precioListaActiva.precioVentaOverride != null || this.precioVentaOverrideDirty) return;
      const next = this.resultadoFormulaPreview(this.precioListaActiva);
      this.precioVentaOverrideActivoEdit = next != null ? this.truncarDosDecimales(Number(next)) : null;
    },
    truncarDosDecimales(value) {
      if (value == null || Number.isNaN(value)) return null;
      return Math.trunc(Number(value) * 100) / 100;
    },
    async savePendingListaOverrides() {
      if (this.operation !== "updating" || !this.precioListaActiva) return;
      const productoId = this.form.id || this.form._id || null;
      if (!productoId) return;
      const listaId = this.precioListaActiva.lista?.id;
      if (!listaId) return;

      if (this.margenOverrideDirty) {
        const margenOverride =
          this.margenOverrideActivoEdit == null || this.margenOverrideActivoEdit === ""
            ? null
            : Number(this.margenOverrideActivoEdit);
        await ListaPreciosVentaProvider.setMargen(
          productoId,
          listaId,
          margenOverride
        );
      }

      if (this.precioVentaOverrideDirty) {
        const calculadoLocal = this.resultadoFormulaPreview(this.precioListaActiva);
        const calculado = Number(calculadoLocal ?? this.precioListaActiva.precioVentaCalculado);
        const input =
          this.precioVentaOverrideActivoEdit == null || this.precioVentaOverrideActivoEdit === ""
            ? null
            : this.truncarDosDecimales(Number(this.precioVentaOverrideActivoEdit));
        const precioVentaOverride =
          input != null && !Number.isNaN(calculado) && Math.abs(input - calculado) <= 0.01
            ? null
            : input;
        await ListaPreciosVentaProvider.setOverride(
          productoId,
          listaId,
          precioVentaOverride
        );
      }
    },
    formulaActivaIncluye(variable) {
      if (this.operation === "updating" && this.precioListaActiva) {
        return this.formulaIncluye(this.precioListaActiva, variable);
      }
      const formula =
        "precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)";
      return new RegExp(`\\b${variable}\\b`).test(formula);
    },
    formulaIncluye(precioLista, variable) {
      const formula =
        precioLista?.lista?.formulaConfig?.formula ||
        "precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)";
      return new RegExp(`\\b${variable}\\b`).test(formula);
    },
    previewListaRow(precioLista, variable) {
      if (!this.formulaIncluye(precioLista, variable)) return false;
      if (variable === "descuento") return Number(this.descuentoProveedorParaCalculo) > 0;
      if (variable === "iva") return Number(this.form.ivaPorcentaje) > 0;
      if (variable === "margen") return Number(this.previewListaValores(precioLista).margen) > 0;
      return true;
    },
    previewListaValores(precioLista) {
      const base = Number(this.precioCompraParaCalculo) || 0;
      const descuento = this.formulaIncluye(precioLista, "descuento")
        ? Number(this.descuentoProveedorParaCalculo) || 0
        : 0;
      const iva = this.formulaIncluye(precioLista, "iva")
        ? Number(this.form.ivaPorcentaje) || 0
        : 0;
      const margenOverride =
        this.esPrecioListaActiva(precioLista)
          ? this.margenOverrideActivoEdit
          : precioLista?.margenOverride;
      const margen = this.formulaIncluye(precioLista, "margen")
        ? Number(
          margenOverride ??
          this.margenEfectivoParaLista(precioLista)
        )
        : 0;
      const tipoRedondeo =
        precioLista?.lista?.formulaConfig?.tipoRedondeo ??
        this.tipoRedondeo ??
        "ninguno";

      const montoDescuento = base * (descuento / 100);
      const despuesDescuento = base - montoDescuento;
      const montoIva = despuesDescuento * (iva / 100);
      const despuesIva = despuesDescuento + montoIva;
      const montoMargen = despuesIva * (margen / 100);
      const antesRedondeo = despuesIva + montoMargen;
      const redondeado = this.aplicarRedondeoPreview(antesRedondeo, tipoRedondeo);

      return {
        margen,
        tipoRedondeo,
        montoDescuento,
        montoIva,
        montoMargen,
        montoRedondeo: Math.max(0, redondeado - antesRedondeo),
      };
    },
    esPrecioListaActiva(precioLista) {
      const activeId = this.precioListaActiva?.lista?.id;
      const listaId = precioLista?.lista?.id;
      return !!activeId && !!listaId && String(activeId) === String(listaId);
    },
    variablesFormulaPreview(precioLista) {
      const valores = this.previewListaValores(precioLista);
      return {
        precio_compra: Number(this.precioCompraParaCalculo) || 0,
        descuento: Number(this.descuentoProveedorParaCalculo) || 0,
        iva: Number(this.form.ivaPorcentaje) || 0,
        margen: valores.margen,
      };
    },
    formulaAplicadaPreview(precioLista) {
      const formula =
        precioLista?.lista?.formulaConfig?.formula ||
        "precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)";
      const vars = this.variablesFormulaPreview(precioLista);
      return formula
        .replace(/\bprecio_compra\b/g, this.formatCurrency(vars.precio_compra))
        .replace(/\bdescuento\b/g, String(vars.descuento))
        .replace(/\biva\b/g, String(vars.iva))
        .replace(/\bmargen\b/g, String(vars.margen));
    },
    resultadoFormulaPreview(precioLista) {
      const formula =
        precioLista?.lista?.formulaConfig?.formula ||
        "precio_compra * (1 - descuento/100) * (1 + iva/100) * (1 + margen/100)";
      const vars = this.variablesFormulaPreview(precioLista);
      try {
        // eslint-disable-next-line no-new-func
        const fn = new Function(
          "precio_compra",
          "descuento",
          "iva",
          "margen",
          `"use strict"; return (${formula});`
        );
        const raw = fn(vars.precio_compra, vars.descuento, vars.iva, vars.margen);
        if (typeof raw !== "number" || !isFinite(raw) || raw < 0) return null;
        const tipoRedondeo = this.previewListaValores(precioLista).tipoRedondeo;
        return this.aplicarRedondeoPreview(raw, tipoRedondeo);
      } catch (e) {
        return null;
      }
    },
    aplicarRedondeoPreview(precio, tipoRedondeo) {
      if (!precio || tipoRedondeo === "ninguno" || !tipoRedondeo) return precio;
      switch (tipoRedondeo) {
        case "centavos":
          return Math.ceil(precio);
        case "multiplo_10":
          return Math.ceil(precio / 10) * 10;
        case "multiplo_100":
          return Math.ceil(precio / 100) * 100;
        default:
          return precio;
      }
    },
    precioFinalLista(precioLista) {
      const resultadoLocal = this.resultadoFormulaPreview(precioLista);
      if (resultadoLocal != null) return resultadoLocal;
      if (precioLista?.precioVentaCalculado != null) return precioLista.precioVentaCalculado;
      if (precioLista?.precioVenta != null) return precioLista.precioVenta;
      const valores = this.previewListaValores(precioLista);
      const base = Number(this.precioCompraParaCalculo) || 0;
      const descuento = this.formulaIncluye(precioLista, "descuento")
        ? Number(this.descuentoProveedorParaCalculo) || 0
        : 0;
      const iva = this.formulaIncluye(precioLista, "iva")
        ? Number(this.form.ivaPorcentaje) || 0
        : 0;
      const despuesDescuento = base * (1 - descuento / 100);
      const despuesIva = despuesDescuento * (1 + iva / 100);
      const antesRedondeo = despuesIva * (1 + valores.margen / 100);
      return this.aplicarRedondeoPreview(antesRedondeo, valores.tipoRedondeo);
    },
    formatPrecioCompraItem(item) {
      if (!item) return "-";
      const n = Number(item.precioCompra);
      if (!Number.isNaN(n)) return "$" + n.toFixed(2);
      return "-";
    },
    obtenerTextoRedondeo(tipo = this.tipoRedondeo) {
      const textos = {
        'centavos': 'centavos',
        'multiplo_10': 'múltiplo de 10',
        'multiplo_100': 'múltiplo de 100'
      };
      return textos[tipo] || '';
    },
    actualizarCodigoBarrasSegunModo(force = false) {
      if (this.modoCodigoBarras === 'manual') return;
      if (this.modoCodigoBarras === 'usarProveedor') {
        if (this.preciosProveedorParaModo.length > 0) {
          if (force || !this.form.codigoBarras) {
            this.form.codigoBarras = this.normalizarCodigoBarras(this.preciosProveedorParaModo[0].codigoBarrasProveedor || '');
          }
          return;
        }
        this.form.codigoBarras = this.normalizarCodigoBarras(this.form.codigoBarrasProveedor || '');
        return;
      }
      if (this.modoCodigoBarras === 'autogenerar') {
        if (this.opcionesCodigoBarrasAutogenerado.length > 0) {
          if (force || !this.form.codigoBarras) {
            this.form.codigoBarras = this.opcionesCodigoBarrasAutogenerado[0].codigoBarras || '';
          }
          return;
        }
        if (this.form.marca && this.form.tamanio && this.form.codigoBarrasProveedor) {
          const valor = this.generarCodigoBarrasPropio(
            this.form.marca,
            this.form.tamanio,
            this.form.codigoBarrasProveedor
          );
          this.form.codigoBarras = this.normalizarCodigoBarras(valor);
        }
      }
    },
    generarCodigoBarrasPropio(marca, tamanio, codigoBarrasProveedor) {
      codigoBarrasProveedor = this.normalizarCodigoBarras(codigoBarrasProveedor);
      if (!codigoBarrasProveedor || codigoBarrasProveedor.includes(',')) {
        return codigoBarrasProveedor || '';
      }
      let codigo = codigoBarrasProveedor.length > 15
        ? codigoBarrasProveedor.substring(0, 15)
        : codigoBarrasProveedor;
      codigo = codigo.toUpperCase();
      const marcaAbreviada = (marca.nombre || '')
        .replace(/\s/g, '')
        .substring(0, 3)
        .toUpperCase();
      const tamanioVal = tamanio && tamanio.tamanio != null ? String(tamanio.tamanio) : '';
      return `${marcaAbreviada} ${codigo} ${tamanioVal}`.trim();
    },
    async cargarPreciosProveedor() {
      if (this.operation !== "updating") return;
      const productoId = this.form.id || this.form._id || this.value?.id || this.value?._id;
      if (!productoId) return;
      try {
        const res = await ProductoProvider.findPreciosProveedor(productoId);
        this.preciosProveedor = res?.data?.productoPreciosProveedor || [];
        this.syncCodigoBarrasProveedorDesdeChips();
      } catch {
        this.preciosProveedor = [];
      }
    },
    async cargarProveedores() {
      this.loadingProveedores = true;
      try {
        const res = await ProveedorProvider.fetchProveedores();
        this.proveedores = res?.data?.proveedorFetch || [];
      } catch {
        this.proveedores = [];
      } finally {
        this.loadingProveedores = false;
      }
    },
    editarCodigoProveedor(item) {
      this.modoEdicionCodigo = true;
      this.editandoCodigoId = item.id;
      this.nuevoCodigo = {
        proveedorId: item.proveedor?.id || item.proveedorId,
        codigoBarrasProveedor: item.codigoBarrasProveedor,
      };
      this.errorCodigoProveedor = "";
    },
    cancelarEdicionCodigo() {
      this.modoEdicionCodigo = false;
      this.editandoCodigoId = null;
      this.nuevoCodigo = { proveedorId: null, codigoBarrasProveedor: "" };
      this.errorCodigoProveedor = "";
    },
    async guardarNuevoCodigo() {
      if (!this.nuevoCodigo.proveedorId || !(this.nuevoCodigo.codigoBarrasProveedor || "").trim()) return;
      const productoId = this.form.id || this.form._id || this.value?.id || this.value?._id;
      this.guardandoCodigo = true;
      this.errorCodigoProveedor = "";
      try {
        if (!productoId) {
          // Crear: agregar/actualizar localmente sin API
          const prov = this.proveedores.find((p) => p.id === this.nuevoCodigo.proveedorId);
          if (this.modoEdicionCodigo && this.editandoCodigoId) {
            this.preciosProveedor = this.preciosProveedor.map((p) =>
              p.id === this.editandoCodigoId
                ? { ...p, codigoBarrasProveedor: (this.nuevoCodigo.codigoBarrasProveedor || "").trim() }
                : p
            );
          } else {
            const nuevo = {
              id: `temp-${Date.now()}`,
              proveedorId: this.nuevoCodigo.proveedorId,
              codigoBarrasProveedor: (this.nuevoCodigo.codigoBarrasProveedor || "").trim(),
              proveedor: prov ? { id: prov.id, nombre: prov.nombre } : null,
              precioCompra: this.precioCompraActivoEdit != null ? Number(this.precioCompraActivoEdit) : null,
              descuentoProveedor: this.form.descuentoProveedor != null ? Number(this.form.descuentoProveedor) : null,
            };
            this.preciosProveedor = [...this.preciosProveedor, nuevo];
          }
          this.syncCodigoBarrasProveedorDesdeChips();
        } else {
          const res = await ProductoProvider.upsertProductoProveedorPrecio({
            productoId,
            proveedorId: this.nuevoCodigo.proveedorId,
            codigoBarrasProveedor: (this.nuevoCodigo.codigoBarrasProveedor || "").trim(),
            precioCompra: this.precioCompraActivoEdit != null && this.precioCompraActivoEdit !== "" ? Number(this.precioCompraActivoEdit) : null,
            descuentoProveedor: this.form.descuentoProveedor != null && this.form.descuentoProveedor !== "" ? Number(this.form.descuentoProveedor) : null,
          });
          const resultado = res?.data?.productoProveedorPrecioUpsert;
          if (resultado) {
            if (this.modoEdicionCodigo && this.editandoCodigoId) {
              this.preciosProveedor = this.preciosProveedor.map((p) =>
                p.id === this.editandoCodigoId ? resultado : p
              );
            } else {
              this.preciosProveedor = [...this.preciosProveedor, resultado];
            }
            this.syncCodigoBarrasProveedorDesdeChips();
          }
        }
        this.cancelarEdicionCodigo();
      } catch (err) {
        this.errorCodigoProveedor = err?.graphQLErrors?.[0]?.message || err?.message || "Error al guardar";
      } finally {
        this.guardandoCodigo = false;
      }
    },
    getCodigoProveedorSelectLabel(item) {
      if (!item) return "-";
      const pp = typeof item === "object" && item != null && "codigoBarrasProveedor" in item
        ? item
        : this.preciosProveedor.find((p) => String(p.id) === String(item));
      if (!pp) return "-";
      const cod = pp.codigoBarrasProveedor || "-";
      const prov = pp.proveedor && pp.proveedor.nombre ? pp.proveedor.nombre : "";
      return prov ? `${cod} (${prov})` : cod;
    },
    syncCodigoBarrasProveedorDesdeChips() {
      if (this.preciosProveedor.length > 0 && this.preciosProveedor[0].codigoBarrasProveedor) {
        this.form.codigoBarrasProveedor = this.normalizarCodigoBarras(this.preciosProveedor[0].codigoBarrasProveedor);
        this.form.proveedorId = this.preciosProveedor[0].proveedorId || this.preciosProveedor[0].proveedor?.id || null;
        if (!this.form.productoProveedorPrecioActivoId && this.preciosProveedor[0].id) {
          this.form.productoProveedorPrecioActivoId = this.preciosProveedor[0].id;
        }
      } else {
        this.form.codigoBarrasProveedor = "";
        this.form.proveedorId = null;
      }
    },
    eliminarCodigoProveedor(pp) {
      if (this.editandoCodigoId === pp.id) this.cancelarEdicionCodigo();
      this.codigosProveedorAEliminar = [pp];
      this.dialogConfirmarEliminarCodigo = true;
    },
    async confirmarEliminarCodigosProveedor() {
      const toRemove = this.codigosProveedorAEliminar || [];
      this.dialogConfirmarEliminarCodigo = false;
      this.codigosProveedorAEliminar = [];
      if (toRemove.length === 0) return;
      if (toRemove.length === 1) {
        await this.eliminarCodigoProveedorSilent(toRemove[0]);
      } else {
        await this.eliminarCodigosProveedorMasivo(toRemove);
      }
    },
    esPrecioProveedorTemporal(pp) {
      const id = pp?.id ?? pp;
      return typeof id === "string" && id.startsWith("temp-");
    },
    async eliminarCodigoProveedorSilent(pp) {
      if (this.esPrecioProveedorTemporal(pp)) {
        this.preciosProveedor = this.preciosProveedor.filter((p) => p.id !== pp.id);
        this.syncCodigoBarrasProveedorDesdeChips();
        return;
      }
      try {
        await ProductoProvider.eliminarProductoProveedorPrecio(pp.id);
        this.preciosProveedor = this.preciosProveedor.filter((p) => p.id !== pp.id);
        this.syncCodigoBarrasProveedorDesdeChips();
      } catch (err) {
        const msg = err?.graphQLErrors?.[0]?.message || err?.message || "Error al eliminar";
        alert(msg);
      }
    },
    async eliminarCodigosProveedorMasivo(removed) {
      const persistidos = removed.filter((pp) => !this.esPrecioProveedorTemporal(pp));
      const idsToRemove = new Set(removed.map((pp) => pp.id));
      this.preciosProveedor = this.preciosProveedor.filter((p) => !idsToRemove.has(p.id));
      this.syncCodigoBarrasProveedorDesdeChips();
      if (persistidos.length > 0) {
        try {
          await Promise.all(persistidos.map((pp) => ProductoProvider.eliminarProductoProveedorPrecio(pp.id)));
        } catch (err) {
          const msg = err?.graphQLErrors?.[0]?.message || err?.message || "Error al eliminar";
          alert(msg);
        }
      }
    },
  },
};
</script>

<style scoped>
.formula-preview-code {
  display: inline-block;
  background: #e3f2fd;
  border-radius: 4px;
  padding: 2px 6px;
  word-break: break-all;
  color: #0d47a1;
  font-size: 0.9rem;
  line-height: 1.45;
}
.formula-preview-aplicada {
  background: #f1f8ff;
  border-left: 3px solid #42a5f5;
  border-radius: 4px;
  color: #1565c0;
  padding: 8px 10px;
}
.precio-preview-card {
  max-width: 33.333%;
}
.precio-preview-warning {
  color: #455a64;
  font-size: 0.95rem;
  font-weight: 500;
}
@media (max-width: 960px) {
  .precio-preview-card {
    max-width: 100%;
  }
}
</style>
